import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";

import {
  saveProjectRecord,
  type ProjectRecord,
  type ProjectRecordKind,
  type ProjectRecordPayload,
} from "@/api/development_suite";
import {
  blankToNull,
  developmentSuiteKeys,
  formatDateTime,
} from "@/components/development_suite/developmentSuiteQueries";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const RECORD_KIND_COPY: Record<
  ProjectRecordKind,
  { singular: string; plural: string; titlePlaceholder: string; description: string }
> = {
  permits: {
    singular: "permit",
    plural: "Permits",
    titlePlaceholder: "e.g. Building permit",
    description: "Each permit is its own request for one authorization.",
  },
  planning_records: {
    singular: "planning record",
    plural: "Planning records",
    titlePlaceholder: "e.g. Rezoning to R-3",
    description: "Planning work such as a rezoning or variance. It does not need a permit.",
  },
};

type ProjectRecordDialogProps = {
  municipalityId: number;
  projectId: number;
  kind: ProjectRecordKind;
  /** The record to open and edit, or null to add one. */
  record: ProjectRecord | null;
  onClose: () => void;
  onSaved: (record: ProjectRecord) => void;
};

/** Mount only while open, so each open starts from the saved values. */
export function ProjectRecordDialog({
  municipalityId,
  projectId,
  kind,
  record,
  onClose,
  onSaved,
}: ProjectRecordDialogProps) {
  const copy = RECORD_KIND_COPY[kind];
  // The dialog unmounts on close, so Radix cannot restore focus; return it to
  // the control that opened the dialog so keyboard users keep their place.
  const openerRef = useRef(document.activeElement as HTMLElement | null);
  useEffect(() => {
    const opener = openerRef.current;
    return () => {
      setTimeout(() => opener?.isConnected && opener.focus(), 0);
    };
  }, []);
  const [title, setTitle] = useState(record?.title ?? "");
  const [reference, setReference] = useState(record?.external_reference ?? "");
  const [titleMissing, setTitleMissing] = useState(false);

  // A failed save leaves the dialog open with what was typed.
  const mutation = useMutation({
    mutationKey: developmentSuiteKeys.all(municipalityId),
    mutationFn: (payload: ProjectRecordPayload) =>
      saveProjectRecord(municipalityId, projectId, kind, record?.id ?? null, payload),
    onSuccess: onSaved,
  });
  const saving = mutation.isPending;
  // Clears only a settled error; resetting a pending save would re-enable Submit mid-write.
  const clearSaveError = () => {
    if (mutation.isError) mutation.reset();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      clearSaveError();
      setTitleMissing(true);
      document.getElementById("record-title")?.focus();
      return;
    }
    setTitleMissing(false);
    mutation.mutate({ title: title.trim(), external_reference: blankToNull(reference) });
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>{record ? record.title : `Add ${copy.singular}`}</DialogTitle>
            <DialogDescription>
              {record
                ? `Changes apply to this ${copy.singular} only.`
                : copy.description}
            </DialogDescription>
          </DialogHeader>

          {record ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md border bg-muted/30 p-3 text-xs">
              <dt className="text-muted-foreground">Added</dt>
              <dd>
                {formatDateTime(record.created_at)}
                {record.created_by ? ` by ${record.created_by.email}` : ""}
              </dd>
              <dt className="text-muted-foreground">Last saved</dt>
              <dd>{formatDateTime(record.updated_at)}</dd>
            </dl>
          ) : null}

          {mutation.error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>The {copy.singular} was not saved</AlertTitle>
              <AlertDescription>
                {mutation.error.message} Your changes are still here, so you can correct them and try again.
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Fields lock while saving so nothing typed mid-save is silently dropped. */}
          <fieldset disabled={saving} className="min-w-0 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="record-title">Title</Label>
              <Input
                id="record-title"
                value={title}
                onChange={(event) => {
                  clearSaveError();
                  setTitleMissing(false);
                  setTitle(event.target.value);
                }}
                placeholder={copy.titlePlaceholder}
                maxLength={255}
                aria-invalid={titleMissing}
                aria-describedby={titleMissing ? "record-title-error" : undefined}
              />
              {titleMissing ? (
                <p id="record-title-error" className="text-sm text-destructive">
                  Enter a title.
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="record-reference">Reference number</Label>
              <Input
                id="record-reference"
                value={reference}
                onChange={(event) => {
                  clearSaveError();
                  setReference(event.target.value);
                }}
                placeholder="Optional, e.g. BP-2026-0142"
                maxLength={255}
                aria-describedby="record-reference-hint"
              />
              <p id="record-reference-hint" className="text-xs text-muted-foreground">
                Use your municipality's case or permit number to tell similar records apart.
              </p>
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : record ? "Save changes" : `Add ${copy.singular}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
